import { Button, Checkbox, Form, Select, Space } from 'antd';
import { ListWrapper, TableContainer } from 'pages/Publications/List/List.styled';
import { FC, useMemo } from 'react';
import { ROUTES } from 'shared/constants/ROUTES';
import { IPagination } from 'shared/hooks/usePagination';
import { IProgramSetup, IProgramSetupResponsePayload } from 'shared/interfaces/IProgramSetup';
import { ITableData } from 'shared/interfaces/utils/ITable';
import { Card, Link } from 'shared/theme/elements';
import Table from 'shared/theme/elements/Table/Table';
import CustomSelect from 'shared/theme/elements/Select/Select';
// import { IKeyValue } from 'shared/interfaces/utils/IKeyValue';

import { LIST_COLUMN } from './ListColumn';
// new add
import {
  ProgramSetupFilter,
  ProgramSetupLoading,
  ProgramSetupSearch,
  ProgramSetupSelectOptions,
} from './ListContainer';
import { toTitleCase } from 'shared/utils/toTitleCase';
// import { log } from 'console';

interface Props {
  data?: IProgramSetupResponsePayload;
  pagination: IPagination;
  onDelete: (
    id: string,
    // channel: Pick<IProgramSetup, 'channel_details'>['channel_details'],
    station: Pick<IProgramSetup, 'station_details'>['station_details'],

    program: Pick<IProgramSetup, 'station_program_details'>['station_program_details'],
  ) => void;
  onEdit: (id: string) => void;
  onFilter: (key: keyof ProgramSetupFilter, value?: any) => void;
  filters: ProgramSetupFilter;
  options: ProgramSetupSelectOptions;
  loading: ProgramSetupLoading;
  onSearch: (key: keyof ProgramSetupSearch, value: any) => void;
  search: ProgramSetupSearch;
  handleSelect: (record: any, selected: string[]) => void;
  toggleSelectAll: (rowsChecked: any) => void;
  selectedRowKeys: string[];
  newSelectedRows: string[];
  locationState: { machine_ip: string; machine_type: string };
  IPOption?: { value: string; label: string }[];
  selectedIP: string;
  handleChangeIP: (value: string) => void;
  transferMachineIP: () => void;
}

const ListView: FC<Props> = ({
  data,
  onDelete,
  onEdit,
  onFilter,
  onSearch,
  search,
  filters,
  options,
  loading,
  pagination,
  handleSelect,
  toggleSelectAll,
  selectedRowKeys,
  newSelectedRows,
  locationState,
  IPOption,
  selectedIP,
  handleChangeIP,
  transferMachineIP,
}) => {
  const tableData = useMemo(() => {
    const results = data?.data ? data.data : [];

    return results.map((result: IProgramSetup) => {
      return {
        ...result,
        key: result._id,
      };
    });
  }, [data]) as ITableData[];

  const headerCheckbox = (
    <Checkbox
      checked={newSelectedRows?.every((v) => selectedRowKeys.includes(v)) && selectedRowKeys.length !== 0}
      onChange={(e) => toggleSelectAll(e)}
      indeterminate={!newSelectedRows?.every((v) => selectedRowKeys.includes(v)) && selectedRowKeys.length !== 0}
    />
  );

  const rowSelection = {
    selectedRowKeys,
    type: 'checkbox',
    onSelect: handleSelect,
    columnTitle: headerCheckbox,
    getCheckboxProps: (record) => ({
      disabled: record.mmi_service_tag,
    }),
  };

  // Convert channel options to IKeyValue format for custom Select component
  const channelItems = useMemo(() => {
    return (
      options.station?.map((option) => ({
        id: option.value as string,
        value: option.label as string,
        disabled: option.disabled,
      })) || []
    );
  }, [options.station]);

  // Ensure the selected value always has a label in the items
  const selectedStation = channelItems.find((opt) => opt.id === filters.station);
  // Find the station_name from the data if not in options
  let fallbackStationName = 'Unknown Station';
  if (!selectedStation && filters.station && data?.data) {
    const found = data.data.find((item) => item.station_details?._id === filters.station);
    if (found) {
      fallbackStationName = found.station_details.station_name;
    }
  }
  const displayItems = selectedStation
    ? channelItems
    : filters.station
    ? [...channelItems, { id: filters.station, value: fallbackStationName }]
    : channelItems;

  return (
    <ListWrapper>
      <Card $fullHeight>
        {/* <Select items={MEDIA_SOURCES} placeholder="Media Source" /> */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <div>
              <Form layout="vertical">
                <Form.Item label="Station">
                  <CustomSelect
                    className="min-w-[200px]"
                    placeHolder="Select Station"
                    items={displayItems}
                    value={filters.station || undefined}
                    onChange={(value) => onFilter('station', value as string)}
                    loading={loading.station}
                    allowClear
                    showSearch
                    filterOption={false}
                    onSearch={(value) => onSearch('station', value)}
                  />
                </Form.Item>
              </Form>
            </div>
            {selectedRowKeys.length ? (
              <Space>
                <div>
                  <Form layout="vertical">
                    <Form.Item
                      label={`Move to other ${locationState?.machine_type === 'recording' ? 'Local' : ''} ${toTitleCase(
                        locationState?.machine_type,
                      )} Machine`}
                    >
                      <Select
                        className="min-w-[200px]"
                        placeholder="Select IP Address"
                        options={IPOption}
                        allowClear
                        showSearch
                        onChange={handleChangeIP}
                        value={selectedIP || undefined}
                      ></Select>
                    </Form.Item>
                  </Form>
                </div>
                <Button
                  type="primary"
                  className="mt-[7px]"
                  disabled={selectedIP?.length === 0}
                  onClick={transferMachineIP}
                >
                  Move
                </Button>
              </Space>
            ) : (
              ''
            )}
          </div>
          {/* <Input type="search" allowClear /> */}
          <Link
            type="link"
            to={{
              pathname: ROUTES.RADIO.PROGRAMSETUP.CREATE,
              state: {
                defaultStationId: filters.station,
                defaultStationName: displayItems.find((item) => item.id === filters.station)?.value,
              },
            }}
            style={{ borderRadius: 9 }}
          >
            Create New Program Setup
          </Link>
        </div>
        <TableContainer>
          <Table
            loading={loading.programSetup}
            data={tableData}
            rowSelection={locationState?.machine_ip ? rowSelection : false}
            columns={LIST_COLUMN({
              handleEdit: onEdit,
              handleDelete: onDelete,
              filters,
              onFilter,
              search,
              onSearch,
              options,
              loading,
              locationState,
            })}
            pagination={{
              current: pagination.page,
              pageSize: pagination.pageSize,
              total: data?.meta?.pagination.total || 0,
              onChange: (newPage: number, currentPageSize: number) => {
                pagination.handlePageChange(newPage);
                pagination.handlePageSizeChange(currentPageSize);
              },
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} results`,
              position: ['topRight', 'bottomRight'],
            }}
          />
        </TableContainer>
      </Card>
    </ListWrapper>
  );
};

export default ListView;
